import { resolveMonthKpiKey } from "../constants/kpi";

/**
 * Shared per-KPI points recompute used wherever we display point totals.
 *
 * Why this exists:
 *   Server-stored `pts` on user_kpi / job.kpis / job.claims / month-rollup rows
 *   can drift from the rule the KPI settings dialog advertises
 *   ("If a player gets {pointValue}, they earn {pointScale} pts" →
 *   pts = amount × pointValue / pointScale). The drift comes from legacy
 *   records and mismatched scoring services.
 *
 * The leaderboard (LiveLeaderboard, Scorekeeper, TV, Past Boards) already
 * recomputes from amt + KPI config. This module is the single source of
 * truth for that recompute so the Feed card, the King of the Mountain
 * climber, and the leaderboard all show the same number.
 *
 * Rule:
 *   - Anchor metrics (metricType === "anchor"):
 *       trust the server's stored `pts` — the server scores them with
 *       benchmarkPoint + Math.floor((amount-anchor)/unit) * above/below
 *       (gmb-server utils/anchorPoints.js), so a naive client recompute would
 *       corrupt it. Only anchors WITHOUT benchmark settings may be re-derived
 *       progressively for a closed month (see recomputeAnchorAsProgressive).
 *   - Otherwise:
 *       pts = amount × (pointValue / pointScale)
 *       falls back to amount × pointValue when pointScale isn't set.
 *   - If the KPI config is missing (defensive — e.g. KPI deleted but a job
 *     row still references it): trust the server's stored `pts` so the row
 *     doesn't silently zero out.
 */

/**
 * Points-per-amount multiplier — byte-for-byte identical to the rule the live
 * leaderboard uses (LiveLeaderboard.jsx:79-83), so anything calling this helper
 * shows the SAME number the leaderboard / Scorekeeper / TV / Past Boards show.
 *
 * Rule (CompanyMetrics / WorkspaceKpi / GameRulesModal convention):
 *   "Every {pointScale} amount earns {pointValue} pts"
 *   → pts per 1 amount = pointValue / pointScale
 *   → falls back to amount × pointValue when pointScale isn't set
 *     (treats pointScale=1 as the implicit "per 1 amount" base case)
 *
 * Examples:
 *   pointScale=1, pointValue=0.1  → 0.1 pts per 1 amount
 *   pointScale=1, pointValue=50   → 50  pts per 1 review
 *   pointScale=2, pointValue=10   → 5   pts per 1 amount
 */
export const ptsPerAmtOf = (kpi) => {
  const s = Number(kpi?.pointScale) || 0;
  const v = Number(kpi?.pointValue) || 0;
  return s > 0 ? v / s : v;
};

/** Anchor with the Benchmark point / per-unit settings: server pts are authoritative. */
export const hasBenchmarkScoring = (kpi) =>
  kpi?.metricType === "anchor" &&
  kpi?.optional?.benchmarkPoint != null &&
  Number.isFinite(Number(kpi.optional.benchmarkPoint));

/**
 * Recompute one score row's pts.
 * @param {object} row    — { amt | amount, pts | points } stored on the server
 * @param {object} kpi    — the KPI's workspace config (with metricType, pointValue, pointScale)
 */
export const recomputeEntryPts = (row, kpi) => {
  if (!row) return 0;
  // No KPI config — keep server pts so we don't silently zero out.
  if (!kpi) return Number(row.pts ?? row.points) || 0;
  if (kpi.metricType === "anchor") return Number(row.pts ?? row.points) || 0;
  const amount = Number(row.amt ?? row.amount) || 0;
  return amount * ptsPerAmtOf(kpi);
};

/**
 * Index a workspace's KPI array by `_id` for fast per-entry lookup.
 */
export const indexKpisById = (groupKpis) => {
  const out = {};
  (groupKpis || []).forEach((k) => {
    if (k && typeof k === "object" && k._id) out[k._id] = k;
  });
  return out;
};

/**
 * Recompute a player's period scores scoped to workspace KPIs.
 * Uses all scoring KPIs (including scorekeeper-only) so hidden metrics still
 * contribute to rank totals while remaining off public KPI columns.
 *
 * @param {object} rawScores
 * @param {object[]} scoringKpis — full workspace KPI list for total calculation
 * @param {{ singleKpiId?: string, recomputeAnchorAsProgressive?: boolean }} [options]
 */
export const recomputeScopedPlayerScores = (
  rawScores,
  scoringKpis,
  { singleKpiId = null, recomputeAnchorAsProgressive = false } = {},
) => {
  if (!rawScores) return { total: 0 };

  const kpiList = singleKpiId
    ? (scoringKpis || []).filter((k) => String(k._id) === String(singleKpiId))
    : (scoringKpis || []);
  if (kpiList.length === 0) return rawScores;

  const scoped = { total: 0 };
  kpiList.forEach((kpi) => {
    const kpiId = kpi._id;
    const key = resolveMonthKpiKey(rawScores, kpiId);
    const original = rawScores[key];
    if (!original) return;

    const amt = Number(original?.amt) || 0;
    const isAnchor = kpi?.metricType === "anchor";
    let recomputedPts;
    // Benchmark anchors are never re-derived: amt × pointValue/pointScale has
    // no relation to benchmark ± whole units (Last Month showed Closed Rate at
    // ~0 pts and AVG Ticket at ~458 while the live board showed 700 / 336).
    if (isAnchor && (!recomputeAnchorAsProgressive || hasBenchmarkScoring(kpi))) {
      recomputedPts = Number(original?.pts) || 0;
    } else {
      // Use the configured rule (amt × pointValue/pointScale) ONLY when BOTH
      // pointValue and pointScale are present and non-zero. If EITHER is
      // missing or zero, trust the server-stored pts — the user's actual
      // score, same as /scorekeeper which always shows the stored value.
      const hasFullConfig =
        Number(kpi?.pointValue) > 0 && Number(kpi?.pointScale) > 0;
      recomputedPts = hasFullConfig
        ? amt * ptsPerAmtOf(kpi)
        : (Number(original?.pts) || 0);
    }
    scoped[key] = { ...original, pts: recomputedPts };
  });

  scoped.total = Object.keys(scoped)
    .filter((k) => k !== "total")
    .reduce((sum, k) => sum + (scoped[k]?.pts || 0), 0);

  return scoped;
};

/**
 * Recompute a monthly-rollup total (`userKpi.month` shape: { total, <kpiId>: { amt, pts }, ... }).
 * Scoped to active workspace KPIs — same rule as the live leaderboard.
 */
export const recomputeMonthTotal = (monthScores, groupKpis) =>
  recomputeScopedPlayerScores(monthScores, groupKpis).total;
